import { useState } from 'react';
import SiteNavBar from '../../components/feature/SiteNavBar';
import SiteFooter from '../../components/feature/SiteFooter';
import { extendedGalleryImages, galleryImages } from '../../mocks/property';
import { useTranslation } from 'react-i18next';

type Category = 'all' | 'outside' | 'inside' | 'details';

const allImages = [
  ...galleryImages.map((img) => ({ ...img, category: 'inside' as Category })),
  ...extendedGalleryImages.map((img) => ({
    ...img,
    category: (img.category === 'Aussen' ? 'outside' : img.category === 'Innen' ? 'inside' : 'details') as Category,
  })),
];

interface LightboxProps {
  images: typeof allImages;
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

function Lightbox({ images, index, onClose, onPrev, onNext }: LightboxProps) {
  const img = images[index];
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close */}
      <button
        className="absolute top-6 right-8 text-white/60 hover:text-[#B59A6A] transition-colors cursor-pointer z-10"
        onClick={onClose}
        aria-label="Schließen"
      >
        <i className="ri-close-line text-2xl" />
      </button>

      {/* Counter */}
      <div className="absolute top-7 left-1/2 -translate-x-1/2">
        <p className="font-sans text-[11px] tracking-widest uppercase text-white/40">
          {index + 1} / {images.length}
        </p>
      </div>

      {/* Prev */}
      <button
        className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center border border-white/20 hover:border-[#B59A6A] text-white/60 hover:text-[#B59A6A] transition-all cursor-pointer z-10"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        aria-label="Vorheriges Bild"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>

      {/* Image */}
      <div
        className="max-w-5xl max-h-[80vh] w-full mx-20 flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={img.url}
          alt={img.alt}
          className="max-h-[75vh] w-full object-contain"
        />
        <div className="flex items-center gap-3 mt-5">
          <div className="w-4 h-px bg-[#B59A6A]" />
          <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-white/50">
            {img.alt}
          </p>
        </div>
      </div>

      {/* Next */}
      <button
        className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center border border-white/20 hover:border-[#B59A6A] text-white/60 hover:text-[#B59A6A] transition-all cursor-pointer z-10"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        aria-label="Nächstes Bild"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>
    </div>
  );
}

export default function GaleriePage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<Category>('all');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const CATEGORIES: { key: Category; label: string }[] = [
    { key: 'all', label: t('galerie.filter.all') },
    { key: 'outside', label: t('galerie.filter.outside') },
    { key: 'inside', label: t('galerie.filter.inside') },
    { key: 'details', label: t('galerie.filter.details') },
  ];

  const filtered = activeCategory === 'all' ? allImages : allImages.filter((img) => img.category === activeCategory);

  const openLightbox = (i: number) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = () => setLightboxIndex((i) => i !== null ? (i - 1 + filtered.length) % filtered.length : 0);
  const nextImage = () => setLightboxIndex((i) => i !== null ? (i + 1) % filtered.length : 0);

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <SiteNavBar />

      {/* Page hero */}
      <section className="relative h-72 flex items-end pb-14 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/ebdd9b28e92ab3730bbe142e81010baf.jpg"
          alt="Galerie Hero"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
        <div className="relative z-10 max-w-7xl mx-auto px-8 w-full">
          <p className="font-sans text-[10px] tracking-[0.35em] uppercase text-[#B59A6A] mb-3">{t('galerie.label')}</p>
          <h1 className="font-serif text-4xl md:text-5xl font-medium text-white">{t('galerie.title')}</h1>
        </div>
      </section>

      {/* Category filter */}
      <div className="border-b border-[#E5DDD3] bg-[#FAFAF7] sticky top-[57px] z-40">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex items-center gap-0">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`font-sans text-[11px] tracking-[0.2em] uppercase py-5 px-6 border-b-2 transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.key
                    ? 'border-[#B59A6A] text-[#B59A6A]'
                    : 'border-transparent text-[#6B6460] hover:text-[#1C1C1C]'
                }`}
              >
                {cat.label}
              </button>
            ))}
            <div className="ml-auto font-sans text-[11px] text-[#BFBAB5] py-5">
              {filtered.length} {t('galerie.photos')}
            </div>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((img, i) => (
            <div
              key={img.id}
              className="group relative overflow-hidden cursor-pointer bg-[#F0EDE8]"
              style={{ height: '300px' }}
              onClick={() => openLightbox(i)}
            >
              <img
                src={img.url}
                alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.04]"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-500" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-12 h-12 flex items-center justify-center border border-white/60">
                  <i className="ri-zoom-in-line text-white text-lg" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-full group-hover:translate-y-0 transition-transform duration-400">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-px bg-[#B59A6A]" />
                  <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-white/80">{img.alt}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-16 pt-10 border-t border-[#E5DDD3]">
          <p className="font-sans text-[11px] tracking-widest uppercase text-[#B59A6A]">{t('galerie.moreOnRequest')}</p>
        </div>
      </div>

      <SiteFooter />

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={filtered}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </div>
  );
}
